import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/sharp-solid-svg-icons';
import { getDataTestId } from 'eone-ui/functions/data-test-ids';
import { isNullOrUndefined } from 'eone-ui/functions/general';
import { TableFieldType, TableSortDirection } from 'eone-ui/tables/table-constants';
import { EmptyTableHeaderRow, TableHeaderRow } from 'eone-ui/tables/table-header';
import React, { useEffect, useState } from 'react';
import { TableRow } from './table-row';
import './tables.css';

function TableFooter(props) {
  if (!props.Text) {
    return null;
  }

  return <div className="eone-ui-table-counter">{props.Text}</div>;
}

function TablePrompt(props) {
  const clickHandler = () => {
    if (props.OnClick) {
      if (props.Data) {
        props.OnClick(props.Data);
      } else {
        props.OnClick();
      }
    }
  };

  if (props.Disabled) {
    return (
      <div
        className="eone-ui-table-empty-link-disabled"
        title={
          props.DisabledMessage ||
          'This feature requires a subscription that your account does not have enabled'
        }
      >
        {props.Label}
      </div>
    );
  }

  return (
    <a
      className="eone-ui-table-empty-link"
      title={props.Title || props.Label}
      onClick={clickHandler}
    >
      {props.Label}
    </a>
  );
}

function TablePrompts(props) {
  if (!props.Prompts || props.Prompts.length === 0) {
    return null;
  }

  return props.Prompts.map((prompt, index) => {
    return (
      <TablePrompt
        key={index}
        Label={prompt.Label}
        Title={prompt.Title}
        Data={prompt.Data}
        Disabled={prompt.Disabled}
        DisabledMessage={prompt.DisabledMessage}
        OnClick={prompt.OnClick}
        DataTestid={getDataTestId(props.DataTestId, prompt.DataTestId)}
      />
    );
  });
}

function TableContainer(props) {
  const getContainerClass = () => {
    let containerClass = 'eone-ui-table-container';
    if (props.BodyHeight) {
      containerClass = 'eone-ui-table-container-with-height';
    }
    if (props.HorizontalScroll) {
      containerClass += ' eone-ui-table-container-horizontal-scroll';
    }
    return containerClass;
  };

  return (
    <div className={getContainerClass()} style={{ height: props.BodyHeight }}>
      {props.children}
      <TableFooter Text={props.FooterText} />
    </div>
  );
}

function Table(props) {
  const [editing, setEditing] = useState(false);
  const [rowSorting, setRowSorting] = useState([]);

  useEffect(() => {
    if (!editing) {
      generateRows();
    }
  }, [props.TableState, props.Rows, props.Columns, props.IdField]);

  useEffect(() => {
    if (!editing) generateRows();
  }, [editing]);

  const changeHandler = (id, fieldName, value) => {
    if (typeof props.ChangeValue === 'function') {
      setEditing(true);
      props.ChangeValue(id, fieldName, value);
    }
  };

  const updateHandler = (id, fieldName, value) => {
    if (typeof props.UpdateValue === 'function') {
      props.UpdateValue(id, fieldName, value);
      setEditing(false);
    }
  };

  const selectRow = (idField) => {
    if (typeof props.SelectRow === 'function') {
      props.SelectRow(idField);
    }
  };

  const generateRows = () => {
    let rows = searchRows();
    rows = filterRows(rows);
    sortRows(rows);
  };

  const isSearched = (row, column) => {
    if (column.FieldName in row && row[column.FieldName]) {
      // todo - handle selects
      // todo - handle integers (should only be equal)
      return String(row[column.FieldName])
        .toLowerCase()
        .includes(props.TableState.Search.toLowerCase());
    }
    return false;
  };

  const isRowSearched = (row, columns) => {
    for (let column = 0, numberOfColumns = columns.length; column < numberOfColumns; column++) {
      if (isSearched(row, columns[column])) {
        return true;
      }
    }
    return false;
  };

  const searchRows = () => {
    if (!props.TableState?.Search) return props.Rows;
    const searchableColumns = props.Columns.filter((column) => column.Searchable);
    if (searchableColumns.length === 0) return props.Rows;
    const rows = [];
    for (let row = 0, numberOfRows = props.Rows.length; row < numberOfRows; row++) {
      if (isRowSearched(props.Rows[row], searchableColumns)) {
        rows.push(props.Rows[row]);
      }
    }
    return rows;
  };

  const isFiltered = (row, fieldName) => {
    if (
      fieldName in row &&
      row[fieldName] !== undefined &&
      props.TableState?.FilterValues[fieldName].length > 0
    ) {
      return props.TableState.FilterValues[fieldName].includes(row[fieldName]);
    }
    return false;
  };

  const isRowFiltered = (row) => {
    const fieldNames = Object.getOwnPropertyNames(props.TableState.FilterValues);
    if (fieldNames.length === 0) return true;

    let filtered = true;
    for (let i = 0, len = fieldNames.length; i < len; i++) {
      if (
        props.TableState.FilterValues[fieldNames[i]] &&
        props.TableState.FilterValues[fieldNames[i]].length > 0 &&
        !isFiltered(row, fieldNames[i])
      ) {
        filtered = false;
      }
    }
    return filtered;
  };

  const filterRows = (rows) => {
    if (!props.TableState?.FilterValues) return rows;
    const filterRows = [];
    for (let row = 0, numberOfRows = rows.length; row < numberOfRows; row++) {
      if (isRowFiltered(rows[row])) {
        filterRows.push(rows[row]);
      }
    }
    return filterRows;
  };

  const getSelectDisplayValue = (row, column) => {
    const value = row[column.FieldName];
    if (!value) return '';
    if (!column.Options) return value;
    const options = column.Options || row[column.RowOptions] || [];
    const option = options.find((item) => item.Key === value);
    if (!option) return value?.toString().toLowerCase();
    if (!option.Value) return value?.toString().toLowerCase();
    return option.Value?.toString().toLowerCase();
  };

  const sortRows = (rows) => {
    const collator = new Intl.Collator(props.Locale, { caseFirst: 'upper' });
    if (!props.TableState?.Sort || !props.TableState?.Sort.FieldName) {
      setSorting(rows);
      return;
    }
    const sortColumn = props.Columns.find(
      (column) => column.FieldName === props.TableState.Sort.FieldName,
    );
    if (!sortColumn) {
      setSorting(rows);
      return;
    }
    switch (sortColumn.FieldType) {
      case TableFieldType.Select:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            return -collator.compare(
              getSelectDisplayValue(a, sortColumn),
              getSelectDisplayValue(b, sortColumn),
            );
          });
        } else {
          rows.sort((a, b) => {
            return collator.compare(
              getSelectDisplayValue(a, sortColumn),
              getSelectDisplayValue(b, sortColumn),
            );
          });
        }
        break;
      case TableFieldType.Checkbox:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            return Boolean(a[sortColumn.FieldName]) < Boolean(b[sortColumn.FieldName]) ? 1 : -1;
          });
        } else {
          rows.sort((a, b) => {
            return Boolean(a[sortColumn.FieldName]) > Boolean(b[sortColumn.FieldName]) ? 1 : -1;
          });
        }
        break;
      case TableFieldType.Integer:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            return Number.parseInt(a[sortColumn.FieldName]) <
              Number.parseInt(b[sortColumn.FieldName])
              ? 1
              : -1;
          });
        } else {
          rows.sort((a, b) => {
            return Number.parseInt(a[sortColumn.FieldName]) >
              Number.parseInt(b[sortColumn.FieldName])
              ? 1
              : -1;
          });
        }
        break;
      case TableFieldType.Date:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            if (!a && !b) return 0;
            const defaultDate = new Date(-8640000000000000); // Minimum date value
            const dateA = a[sortColumn.FieldName] ? new Date(a[sortColumn.FieldName]) : defaultDate;
            const dateB = b[sortColumn.FieldName] ? new Date(b[sortColumn.FieldName]) : defaultDate;
            return dateB.getTime() - dateA.getTime();
          });
        } else {
          rows.sort((a, b) => {
            if (!a && !b) return 0;
            const defaultDate = new Date(-8640000000000000); // Minimum date value
            const dateA = a[sortColumn.FieldName] ? new Date(a[sortColumn.FieldName]) : defaultDate;
            const dateB = b[sortColumn.FieldName] ? new Date(b[sortColumn.FieldName]) : defaultDate;
            return dateA.getTime() - dateB.getTime();
          });
        }
        break;
      default:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            return -1 * collator.compare(a[sortColumn.FieldName], b[sortColumn.FieldName]);
          });
        } else {
          rows.sort((a, b) => {
            return collator.compare(a[sortColumn.FieldName], b[sortColumn.FieldName]);
          });
        }
        break;
    }
    setSorting(rows);
  };

  const setSorting = (rows) => {
    const newRowSorting = [];
    for (let i = 0, length = rows.length; i < length; i++) {
      newRowSorting.push(rows[i][props.IdField]);
    }
    setRowSorting(newRowSorting);
  };

  const [expandedRows, setExpandedRows] = useState({});

  const handleExpand = (id) => {
    setExpandedRows((prevState) => ({ ...prevState, [id]: !prevState[id] }));
  };

  const renderRow = (row, index, depth = 0) => {
    const hasSubRows = row[props.SubRowField].length > 0;

    return (
      <React.Fragment>
        <TableRow
          Depth={depth}
          ChildPadding={props.ChildPadding ?? 30}
          key={index}
          Row={row}
          Columns={props.Columns}
          SelectedColumns={props.TableState?.Columns}
          SelectRow={selectRow}
          Selected={
            props.IdField &&
            !isNullOrUndefined(props.SelectedRowId) &&
            row[props.IdField] === props.SelectedRowId
          }
          Buttons={props.Buttons}
          IdField={props.IdField}
          Locale={props.Locale}
          ChangeValue={changeHandler}
          UpdateValue={updateHandler}
          ButtonWidth={buttonWidth + 12}
          DataTestId={props.DataTestId}
          ShowExpand={hasSubRows}
          IsExpanded={expandedRows[row[props.IdField]] == true}
          HandleExpand={handleExpand}
        />
        {hasSubRows &&
          expandedRows[row[props.IdField]] &&
          row[props.SubRowField].map((subRow) => renderRow(subRow, index, depth + 1))}
      </React.Fragment>
    );
  };

  const getFilterValues = () => {
    const filterValues = {};
    for (let i = 0, len = props.Columns.length; i < len; i++) {
      // only get filter values for selected columns
      if (
        props.Columns[i].Filterable &&
        (!props.TableState?.Columns ||
          props.TableState.Columns.length === 0 ||
          props.TableState.Columns.includes(props.Columns[i].FieldName))
      ) {
        const columnFilterValues = getColumnFilterValues(props.Columns[i]);
        columnFilterValues.sort((a, b) => {
          return a.Value?.toLowerCase() > b.Value?.toLowerCase() ? 1 : -1;
        });
        filterValues[props.Columns[i].FieldName] = columnFilterValues;
      }
    }
    return filterValues;
  };

  const isColumnFilterValueSelected = (fieldName, value) => {
    if (!props.TableState?.FilterValues) return false;
    if (!(fieldName in props.TableState.FilterValues)) return false;
    if (!props.TableState.FilterValues[fieldName]) return false;
    return props.TableState.FilterValues[fieldName].includes(value);
  };

  const getColumnFilterValues = (column) => {
    let filterValues = [];
    switch (column.FieldType) {
      case TableFieldType.Select:
        filterValues = getColumnFilterSelectValues(column);
        break;
      case TableFieldType.Checkbox:
        filterValues = [
          { Key: false, Value: 'No' },
          { Key: true, Value: 'Yes' },
        ];
        break;
      case TableFieldType.Date:
        // todo
        break;
      case TableFieldType.Integer:
        // todo
        break;
      default:
        filterValues = getColumnFilterStringValues(column);
        break;
    }

    return filterValues.map((filterValue) => {
      return {
        Key: filterValue.Key,
        Value: filterValue.Value,
        Selected: isColumnFilterValueSelected(column.FieldName, filterValue.Key),
      };
    });
  };

  const getColumnFilterSelectValues = (column) => {
    // todo - remove unused values
    // todo - add none if blank values exist
    // todo - sort options
    let options = column.Options.slice(0);
    options = options.filter((option) => option.Key?.toString().substring(0) !== '-'); // remove separators

    return options;
  };

  const getColumnFilterStringValues = (column) => {
    const filterValues = [];
    let includeNone = false;
    for (let i = 0, len = props.Rows.length; i < len; i++) {
      const value = props.Rows[i][column.FieldName];
      if (value) {
        if (!filterValues.includes(value)) {
          filterValues.push(value);
        }
      } else {
        includeNone = true;
      }
    }
    if (includeNone) {
      // todo
    }
    return filterValues.map((filterValue, index) => {
      console.log(index);
      return {
        Key: filterValue,
        Value: filterValue,
      };
    });
  };

  const getFooterText = (filteredRowCount, rowCount) => {
    if (props.FooterText) return props.FooterText;
    if (props.ShowRecordCount) {
      if (filteredRowCount !== rowCount) {
        if (rowCount === 1) {
          return `${filteredRowCount} of ${rowCount} ${props.ItemName}`;
        } else {
          return `${filteredRowCount} of ${rowCount} ${props.ItemPlural}`;
        }
      } else {
        if (filteredRowCount === 1) {
          return `${filteredRowCount} ${props.ItemName}`;
        } else {
          return `${filteredRowCount} ${props.ItemPlural}`;
        }
      }
    }
    return '';
  };

  const getTableClass = () => {
    let tableClass = 'eone-ui-table';
    if (props.HorizontalScroll) {
      tableClass += ' eone-ui-table-horizontal-scroll';
    }
    return tableClass;
  };

  if (props.Visible !== undefined && !props.Visible) {
    return null;
  }

  if (props.Loading) {
    return (
      <div className="eone-ui-table-loading" style={{ height: props.BodyHeight }}>
        <div>
          <FontAwesomeIcon icon={faSpinnerThird} spin />
          <label
            className="eone-ui-table-loading-prompt"
            data-testid={getDataTestId(props.DataTestId, 'loading')}
          >
            {props.LoadingMessage || 'Loading...'}
          </label>
        </div>
      </div>
    );
  }

  if (props.Rows.length === 0) {
    return (
      <div style={{ height: props.BodyHeight }}>
        <EmptyTableHeaderRow
          Visible={!props.HideTableHeader}
          Columns={props.Columns}
          SelectedColumns={props.TableState?.Columns}
          DataTestId={props.DataTestId}
        />
        <div
          className={
            props.HideTableHeader ? 'eone-ui-table-empty-no-header' : 'eone-ui-table-empty'
          }
        >
          <label className="eone-ui-table-empty-prompt">{props.EmptyMessage}</label>
          <div className="eone-ui-table-empty-link-container">
            <TablePrompts Prompts={props.EmptyPrompts} DataTestId={props.DataTestId} />
          </div>
        </div>
      </div>
    );
  }

  const footerText = getFooterText(rowSorting.length, props.Rows.length);
  const maxRowButtonCount = () => {
    let visibleButtonCount = 0;
    if (props.Buttons) {
      for (const btn of props.Buttons) {
        if (btn.Hidden != undefined && btn.Hidden != '') {
          for (const row of props.Rows) {
            const hidden = row[btn.Hidden];
            if (!hidden) {
              visibleButtonCount++;
              break;
            }
          }
        } else visibleButtonCount++;
      }
    }
    return visibleButtonCount;
  };

  let buttonWidth = 0;
  if (props.Buttons) {
    const buttonLength = maxRowButtonCount();
    buttonWidth = buttonLength > 0 ? 44 + (buttonLength - 1) * 43 : 0;
  }

  return (
    <TableContainer
      BodyHeight={props.BodyHeight}
      HorizontalScroll={props.HorizontalScroll}
      FooterText={footerText}
    >
      <table className={getTableClass()}>
        <TableHeaderRow
          Visible={!props.HideTableHeader}
          Columns={props.Columns}
          Sticky={props.BodyHeight}
          Buttons={props.Buttons}
          SelectedColumns={props.TableState?.Columns}
          Filter={props.TableState?.SetFilterValues}
          FilterValues={getFilterValues()}
          Sorting={props.TableState?.Sort}
          Sort={props.TableState?.SetSort}
          ButtonWidth={buttonWidth}
          DataTestId={props.DataTestId}
        />
        <tbody>{props.Rows.map((row, index) => renderRow(row, index))}</tbody>
      </table>
    </TableContainer>
  );
}

export { Table };
